import { api as defaultApi, ApiResponse, Token } from './api';

export interface ResourceApi {
    fields(): Promise<ApiResponse>;

    get(lang: string | null): Promise<ApiResponse>;

    getByID(id: number, lang: string | null): Promise<ApiResponse>;

    getBy(field: string, value: any, lang: string | null): Promise<ApiResponse>;

    getAutocomplete(
        field: string,
        value: any,
        lang: string | null,
    ): Promise<ApiResponse>;

    post(data: Record<string, unknown>): Promise<ApiResponse>;

    put(data: any): Promise<ApiResponse>;

    patch(data: any): Promise<ApiResponse>;

    putLanguage(lang: string, data: any): Promise<ApiResponse>;

    patchLanguage(lang: string, data: any): Promise<ApiResponse>;

    delete(id: number): Promise<ApiResponse>;

    abortRunningRequest(): void;
}

export interface UploadApi {
    getFormData(input: HTMLInputElement): FormData;

    file(input: HTMLInputElement): Promise<ApiResponse | Response>;
}

export interface MetaApi {
    languages(): Promise<ApiResponse>;

    resources(): Promise<ApiResponse>;
}

export interface WidgetGroupApi {
    post(data: Record<string, unknown>): Promise<ApiResponse>;

    putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse>;

    postBulk(data: Record<number, unknown>): Promise<ApiResponse>;

    putLanguageBulk(lang: string, data: Record<number, unknown>): Promise<ApiResponse>;
}

export interface BulkApi {
    /**
     * bulk inserts
     * @param data
     * @return {Promise<Response | ApiResponse>}
     */
    insert(data: Record<string, unknown>): Promise<ApiResponse | Response>;

    /**
     * bulk updates
     * @param data array of updates
     * @return {Promise<Response | ApiResponse>}
     */
    update(data: Record<string, unknown>): Promise<ApiResponse | Response>;

    /**
     * bulk deletion
     * @return {Promise<Response | ApiResponse>}
     * @param conditionField
     * @param conditionId
     */
    delete(conditionField: string, conditionId: number): Promise<ApiResponse | Response>;
}

export interface FormApi {
    send(form: string, data: Record<string, unknown>): Promise<ApiResponse>;
}

export interface ApiClient {
    meta: MetaApi;
    upload: UploadApi;
    resource: (resource: string) => ResourceApi;
    form: FormApi;
    widget_group: (pwgid: number) => WidgetGroupApi;
    bulk: (resource: string) => BulkApi;
}

export const ApiClientFactory = ((basepath: string|null = null, api = defaultApi): ApiClient => {
    let url: string = basepath || `/${document.location.hostname}`;

    // remove trailing slash
    if (url.split('').reverse()[0] === '/') {
        const tempUrl = url.split('');
        tempUrl.pop();
        url = tempUrl.join('');
    }

    /**
     * resource api calls
     * meant to cover about 95% of all our needs
     */
    class Resource implements ResourceApi {
        private url: string;

        private readonly resource: string;

        /**
         * Resource constructor
         * @param url
         * @param resource
         */
        // eslint-disable-next-line no-shadow
        constructor(url: string, resource: string) {
            this.url = url;
            this.resource = resource;
        }

        async fields(): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources/${this.resource}/fields`);
        }

        async get(lang: string|null = null): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources/${this.resource}`, lang);
        }

        async getByID(id: number, lang: string|null = null): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources/${this.resource}/${id}`, lang);
        }

        async getBy(field: string, value: any, lang: string|null = null): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources/${this.resource}/${field}/${value}`, lang);
        }

        async getAutocomplete(
            field: string,
            value: any,
            lang: string|null = null,
        ): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources_autocomplete/${this.resource}/${field}/${value}`, lang);
        }

        async post(data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_post(`${url}/api/resources/${this.resource}`, data);
        }

        async put(data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id')) {
                return api.api_put(`${url}/api/resources/${this.resource}/${data.id}`, data, null);
            }
            throw new Error('data must have an id, use post to add new data first');
        }

        async patch(data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id')) {
                return api.api_patch(`${url}/api/resources/${this.resource}/${data.id}`, data, null);
            }
            throw new Error('data must have an id, use post to add new data first');
        }

        async putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id') && data.id !== null) {
                return api.api_put(`${url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
            }
            throw new Error('data must have an id');
        }

        async patchLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            if (data.hasOwnProperty('id') && data.id !== null) {
                return api.api_patch(`${url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
            }
            console.log(data);
            throw new Error('data must have an id');
        }

        async delete(id: number): Promise<ApiResponse> {
            return api.api_delete(`${url}/api/resources/${this.resource}/${id}`);
        }

        // enable aborting a running api request
        abortRunningRequest = api.abort_request.bind(api);
    }

    /**
     * todo: list usages in comment
     */
    class Upload implements UploadApi {
        private url: string;

        // eslint-disable-next-line no-shadow
        constructor(url: string) {
            this.url = url;
        }

        getFormData(input: HTMLInputElement): FormData {
            const data = new FormData();
            data.append('file', input.files[0]);
            return data;
        }

        file(input: HTMLInputElement): Promise<ApiResponse|Response> {
            const data = this.getFormData(input);
            console.log(data);
            console.log(input.files);

            // does not use api.post() because we dont submit json data here
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${url}/api/upload`, {
                    body: data,
                    method: 'post',
                    headers,
                });
            });
        }
    }

    /**
     * todo: list usages in comment
     */
    class Meta implements MetaApi {
        private url: string;

        // eslint-disable-next-line no-shadow
        constructor(url: string) {
            this.url = url;
        }

        async languages(): Promise<ApiResponse> {
            return api.api_get(`${url}/api/languages`);
        }

        async resources(): Promise<ApiResponse> {
            return api.api_get(`${url}/api/resources`);
        }
    }

    /**
     * todo: list usages in comment
     */
    class Form implements FormApi {
        private url: string;

        // eslint-disable-next-line no-shadow
        constructor(url: string) {
            this.url = url;
        }

        async send(form: string, data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_post(`${url}/api/form/${form}`, data);
        }
    }

    /**
     *
     */
    class WidgetGroup implements WidgetGroupApi {
        private url: string;

        private readonly pwgid: number;

        // eslint-disable-next-line no-shadow
        constructor(url: string, pwgid: number) {
            this.url = url;
            this.pwgid = pwgid;
        }

        async post(data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_post(`${url}/api/widget_group/${this.pwgid}`, data);
        }

        async putLanguage(lang: string, data: Record<string, unknown>): Promise<ApiResponse> {
            return api.api_put(`${url}/api/widget_group/${this.pwgid}`, data, lang);
        }

        async postBulk(data: Record<number, unknown>): Promise<ApiResponse> {
            return api.api_post(`${url}/api/widget_group/bulk`, data);
        }

        async putLanguageBulk(lang: string, data: Record<number, unknown>): Promise<ApiResponse> {
            return api.api_put(`${url}/api/widget_group/bulk`, data, lang);
        }
    }

    /**
     *
     */
    class Bulk implements BulkApi {
        private url: string;

        private readonly resource: string;

        /**
         * Bulk constructor
         * @param url
         * @param resource
         */
        // eslint-disable-next-line no-shadow
        constructor(url: string, resource: string) {
            this.url = url;
            this.resource = resource;
        }

        /**
         * bulk inserts
         * @param data
         * @return {Promise<Response | never>}
         */
        async insert(data: Record<string, unknown>): Promise<ApiResponse|Response> {
            // does not use api.post() because we need the bulk insert endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${url}/api/bulk/${this.resource}`, {
                    body: JSON.stringify(data),
                    method: 'post',
                    headers,
                });
            });
        }

        /**
         * bulk updates
         * @param data array of updates
         * @return {Promise<Response | never>}
         */
        async update(data: Record<string, unknown>): Promise<ApiResponse|Response> {
            // does not use api.put() because we need the bulk update endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${url}/api/bulk/${this.resource}`, {
                    body: JSON.stringify(data),
                    method: 'put',
                    headers,
                });
            });
        }

        /**
         * bulk deletion
         * @return {Promise<Response | never>}
         * @param conditionField
         * @param conditionId
         */
        async delete(conditionField: string, conditionId: number): Promise<ApiResponse|Response> {
            // does not use api.delete() because we need the bulk delete endpoint
            return api.request_token().then((t: Token) => t.token).then((token: string) => {
                const headers = new Headers({
                    Authorization: `Bearer ${token}`,
                });

                return fetch(`${url}/api/bulk/${this.resource}/${conditionField}/${conditionId}`, {
                    method: 'delete',
                    headers,
                });
            });
        }
    }

    return {
        meta: new Meta(url),
        upload: new Upload(url),
        resource: (resource: string): Resource => new Resource(url, resource),
        form: new Form(url),
        widget_group: (pwgid: number): WidgetGroup => new WidgetGroup(url, pwgid),
        bulk: (resource: string): Bulk => new Bulk(url, resource),
    };
});
